import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContextDataSelector } from 'src/contexts/CatalogDataContext/CatalogDataContext.selectors';
import { useRetailSaleListingTickets } from 'src/hooks/api/useRetailSaleListingTickets';
import { useSelectedEvents } from 'src/hooks/useSelectedEvents';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import { getEventPerformerVenue } from 'src/utils/eventWithDataUtils';
import { newBigIntId } from 'src/utils/idUtils';
import {
  AccountTaxRateClient,
  Feature,
  ListingQuery,
  MarketplacePaymentLineType,
  MarketplacePaymentState,
  SaleCostType,
  SaleLineItem,
} from 'src/WebApiController';

import { useRetailSaleContext } from '../RetailSaleLocalContext';
import {
  computeRetailTaxAmount,
  getRetailTaxableAmount,
} from '../RetailSaleLocalContext.utils';
import { RetailSaleFormValues, RetailSalePaymentLine } from '../types';

const roundTo2 = (n: number) => Math.round(n * 100) / 100;

/**
 * Resolves the tax rate (a decimal fraction, e.g. 0.0825 = 8.25%) from the account-level
 * tax rules (POS-8561) using the venue location (country code, state/province, postal code)
 * of the selected event — when multiple events are selected, the first event's venue is used.
 * Defaults to 0 when the venue can't be resolved or no rule matches; a resolution failure
 * must not block checkout, so errors also collapse to 0.
 */
export const useTaxRateByLocation = (enabled: boolean) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { event, isMultiListing } = useRetailSaleContext();
  const { watch } = useFormContext<RetailSaleFormValues>();
  const selectedEvents = useSelectedEvents();
  const eventTransformed = useCatalogDataContextDataSelector();

  // Multi-listing mode has no event prop — resolve the first selected event through the
  // listing-tickets query (same key as MultiListingRetailSaleCheckoutStep, so it is served
  // from the React Query cache) and look its venue up in the catalog.
  const allListingIds = watch('allListingIds');
  const primaryListingId = watch('listingId');
  const filterQuery = useMemo((): ListingQuery | null => {
    if (!enabled || !isMultiListing || event != null) return null;
    const ids =
      allListingIds && allListingIds.length > 0
        ? allListingIds
        : primaryListingId && primaryListingId > 0
          ? [primaryListingId]
          : [];
    if (ids.length === 0) return null;
    return { ...EmptyListingQuery, entityIds: ids };
  }, [allListingIds, enabled, event, isMultiListing, primaryListingId]);
  const { data: listingDetails } = useRetailSaleListingTickets(filterQuery);

  const venue = useMemo(() => {
    const firstEvent = selectedEvents?.[0]?.event;
    const selectedEventId =
      firstEvent?.virtId ?? listingDetails?.[0]?.viagVirtId;
    const { venue } = getEventPerformerVenue(selectedEventId, eventTransformed);
    return venue;
  }, [selectedEvents, eventTransformed, listingDetails]);

  const countryCode = venue?.ctryCode ?? null;
  const stateProvince = venue?.state ?? null;
  const zipCode = venue?.pstlCode ?? null;

  const { data: taxRate } = useQuery({
    queryKey: [
      'AccountTaxRateClient.getTaxRateByLocation',
      activeAccountWebClientConfig.activeAccountId,
      countryCode,
      stateProvince,
      zipCode,
    ],
    queryFn: async () =>
      await new AccountTaxRateClient(
        activeAccountWebClientConfig
      ).getTaxRateByLocation(countryCode, stateProvince, zipCode),
    enabled:
      enabled &&
      activeAccountWebClientConfig.activeAccountId != null &&
      !!countryCode,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  return taxRate ?? 0;
};

export const useRetailLineItemOperations = () => {
  const { loginContext } = useAppContext();
  const { setValue, watch, getValues } = useFormContext<RetailSaleFormValues>();
  const hasRetailTaxRate = useUserHasFeature(Feature.RetailTaxRate);

  const marketplacePaymentLines = watch('marketplacePaymentLines');
  const totalNetProceeds = watch('totalNetProceeds');
  const currencyCode =
    watch('currencyCode') ??
    loginContext?.user?.activeAccount.currencyCode ??
    'USD';

  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false);

  // Auto-generated buyer tax (POS-8561). The Tax line is a managed, read-only entry kept inside
  // `marketplacePaymentLines` so it flows into every total (Charge Today, Adyen/external charge
  // amount, invoice) and the submission without any per-callsite change. It is recomputed as the
  // rate (event venue location) or the taxable base (proceeds + manual charges/credits) changes.
  const taxRate = useTaxRateByLocation(hasRetailTaxRate);
  const taxLineIdRef = useRef<number | null>(null);

  // Taxable base = proceeds + net of the manual (non-Tax) charges/credits. Excluding the Tax line
  // keeps the calculation idempotent (no feedback loop when the recomputed line is written back).
  const taxableAmount = useMemo(
    () => getRetailTaxableAmount(totalNetProceeds, marketplacePaymentLines),
    [marketplacePaymentLines, totalNetProceeds]
  );

  const taxAmount = useMemo(
    () => computeRetailTaxAmount(taxRate, taxableAmount),
    [taxRate, taxableAmount]
  );
  const taxRatePercent = useMemo(() => roundTo2(taxRate * 100), [taxRate]);

  // Upsert the managed Tax line (always present — amount may be 0), keeping it last in the array.
  useEffect(() => {
    if (!hasRetailTaxRate) {
      return;
    }
    const current = getValues('marketplacePaymentLines') ?? [];
    const existingTax = current.find(
      (l) => l.paymentLineType === MarketplacePaymentLineType.Tax
    );
    if (existingTax != null) {
      taxLineIdRef.current = existingTax.marketplacePaymentLineId;
    } else if (taxLineIdRef.current == null) {
      taxLineIdRef.current = newBigIntId();
    }

    // Already in sync — skip the write to avoid a render loop.
    if (
      existingTax != null &&
      existingTax.amount === taxAmount &&
      existingTax.ratePercent === taxRatePercent &&
      existingTax.currency === currencyCode
    ) {
      return;
    }

    const taxLine: RetailSalePaymentLine = {
      ...existingTax,
      marketplacePaymentLineId: taxLineIdRef.current!,
      marketplacePaymentId: null,
      externalPaymentId: '',
      externalPaymentLineId:
        existingTax?.externalPaymentLineId ?? `taxline-${Date.now()}`,
      externalSaleId: '',
      saleId: null,
      currency: currencyCode,
      amount: taxAmount,
      isCredit: false,
      paymentLineType: MarketplacePaymentLineType.Tax,
      paymentState: null,
      paymentDate: null,
      receivedDate: null,
      isLocked: false,
      ratePercent: taxRatePercent,
      description: 'Tax',
      saleLineItemChargeReason: null,
      saleLineItemCreditReason: null,
      externalPaymentMethodType: null,
      externalPaymentNote: null,
    };

    const nonTaxLines = current.filter(
      (l) => l.paymentLineType !== MarketplacePaymentLineType.Tax
    );
    setValue('marketplacePaymentLines', [...nonTaxLines, taxLine]);
    // `marketplacePaymentLines` is included so the Tax line is re-added if the array is reset/cleared;
    // the in-sync guard above makes the write idempotent, so this cannot loop.
  }, [
    marketplacePaymentLines,
    taxAmount,
    taxRatePercent,
    currencyCode,
    getValues,
    setValue,
    hasRetailTaxRate,
  ]);

  const openLineItemModal = useCallback(() => setIsLineItemModalOpen(true), []);
  const closeLineItemModal = useCallback(
    () => setIsLineItemModalOpen(false),
    []
  );

  const updateLineItem = useCallback(
    (lineId: number | null, updates: Partial<RetailSalePaymentLine>) => {
      const current = getValues('marketplacePaymentLines') ?? [];
      setValue(
        'marketplacePaymentLines',
        current.map((l) =>
          l.marketplacePaymentLineId === lineId ? { ...l, ...updates } : l
        )
      );
    },
    [getValues, setValue]
  );

  const onLineAmountChange = useCallback(
    (line: RetailSalePaymentLine, absAmount: number) => {
      const signedAmount = line.isCredit ? absAmount : -absAmount;
      const rate = totalNetProceeds
        ? roundTo2((signedAmount / totalNetProceeds) * 100)
        : null;
      updateLineItem(line.marketplacePaymentLineId, {
        amount: signedAmount,
        ratePercent: rate,
      });
    },
    [totalNetProceeds, updateLineItem]
  );

  const onLineRateChange = useCallback(
    (line: RetailSalePaymentLine, rate: number | null) => {
      if (rate === null) {
        updateLineItem(line.marketplacePaymentLineId, { ratePercent: null });
        return;
      }
      const newAmount = totalNetProceeds
        ? roundTo2((rate / 100) * totalNetProceeds)
        : line.amount;
      updateLineItem(line.marketplacePaymentLineId, {
        ratePercent: rate,
        amount: newAmount,
      });
    },
    [totalNetProceeds, updateLineItem]
  );

  const onSaveLineItem = useCallback(
    (item: SaleLineItem) => {
      const isCredit = item.costType === SaleCostType.Credit;
      // POS-8987: Seller-perspective. Credit (fee) = positive,
      // Charge (discount) = negative.
      const signedAmount = isCredit
        ? Math.abs(item.cost)
        : -Math.abs(item.cost);
      const rate = totalNetProceeds
        ? roundTo2((signedAmount / totalNetProceeds) * 100)
        : null;
      const newLine: RetailSalePaymentLine = {
        marketplacePaymentLineId: item.id || newBigIntId(),
        marketplacePaymentId: null,
        externalPaymentId: item.paymentReferenceId ?? '',
        externalPaymentLineId: `paymentline-${Date.now()}`,
        externalSaleId: '',
        saleId: null,
        currency: item.currencyCode,
        amount: signedAmount,
        isCredit: isCredit,
        paymentLineType: isCredit
          ? MarketplacePaymentLineType.Credit
          : MarketplacePaymentLineType.Charge,
        paymentState: item.paymentReceived
          ? MarketplacePaymentState.Paid
          : null,
        paymentDate: item.costDate,
        receivedDate: null,
        isLocked: item.isMarketplacePaymentLocked,
        ratePercent: rate,
        saleLineItemChargeReason: item.saleLineItemChargeReason ?? null,
        saleLineItemCreditReason: item.saleLineItemCreditReason ?? null,
        description: item.description || (isCredit ? 'Credit' : 'Charge'),
        externalPaymentMethodType: null,
        externalPaymentNote: null,
      };
      const current = getValues('marketplacePaymentLines') ?? [];
      setValue('marketplacePaymentLines', [...current, newLine]);
      closeLineItemModal();
    },
    [closeLineItemModal, getValues, setValue, totalNetProceeds]
  );

  const onDeleteLineItem = useCallback(
    (lineId: number | null) => {
      const current = getValues('marketplacePaymentLines') ?? [];
      setValue(
        'marketplacePaymentLines',
        current.filter((l) => l.marketplacePaymentLineId !== lineId)
      );
    },
    [getValues, setValue]
  );

  const totalAmount = useMemo(() => {
    return (
      (totalNetProceeds ?? 0) +
      (marketplacePaymentLines ?? []).reduce(
        (sum, l) => sum + (l.amount ?? 0),
        0
      )
    );
  }, [marketplacePaymentLines, totalNetProceeds]);

  return {
    marketplacePaymentLines,
    totalNetProceeds,
    currencyCode,
    isLineItemModalOpen,
    openLineItemModal,
    closeLineItemModal,
    onLineAmountChange,
    onLineRateChange,
    onSaveLineItem,
    onDeleteLineItem,
    totalAmount,
  };
};
